import { Component, inject, OnInit } from '@angular/core';
import { ImageUploaderComponent } from '../../../../../shared/image-uploader/image-uploader.component';
import { FormsModule, NgForm } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { SettingsApiService } from '../../../services/settings-api.service';
import { VendorDetails } from '../../../models/vendor-details';
import { ToastNotificationService } from '../../../../../notifications/services/toast-notification.service';
import { ToastNotificationType, ToastPosition } from '../../../../../notifications/models/toast-notification-type';
import { GoogleMapComponent } from "../../../../../shared/map/google-map/google-map.component";
import { OpeningHours } from '../../../models/opening-hours';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../../../../shared/services/translations/language.service';
import { VendorProfileService } from '../../../../../shared/services/vendor-profile.service';
import { ApiErrorService } from '../../../../../shared/services/api-error.service';

@Component({
  selector: 'app-settings',
  imports: [CommonModule, FormsModule, ImageUploaderComponent, GoogleMapComponent, TranslateModule],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.css'
})
export class SettingsComponent implements OnInit {

  disableButtons = false;
  loading = false;
  showWorkingHoursErrors = false;

  cities: { id: number, name: string }[] = [
    { id: 0, name: "عمان" },
    { id: 1, name: "اربد" },
    { id: 2, name: "الزرقاء" },
    { id: 3, name: "المفرق" },
    { id: 4, name: "عجلون" },
    { id: 5, name: "جرش" },
    { id: 6, name: "مادبا" },
    { id: 7, name: "البلقاء" },
    { id: 8, name: "الكرك" },
    { id: 9, name: "الطفيلة" },
    { id: 10, name: "معان" },
    { id: 11, name: "العقبة" },
  ];

  weekDays = [
    { id: 0, name: "settings.weekdays.sunday" },
    { id: 1, name: "settings.weekdays.monday" },
    { id: 2, name: "settings.weekdays.tuesday" },
    { id: 3, name: "settings.weekdays.wednesday" },
    { id: 4, name: "settings.weekdays.thursday" },
    { id: 5, name: "settings.weekdays.friday" },
    { id: 6, name: "settings.weekdays.saturday" },
  ];

  neighborhoods: { id: number, name: string }[] = [];
  citySelected = false;

  logo: string[] = [];
  commercialDocument: File | null = null;


  apiService = inject(SettingsApiService);
  apiErrorService = inject(ApiErrorService);
  translate = inject(TranslateService);
  languageService = inject(LanguageService);
  vendorProfile = inject(VendorProfileService);


  originalVendorDetails: VendorDetails | undefined;
  modifiedVendorDetails: VendorDetails | undefined;

  toastNotificationService = inject(ToastNotificationService);


  ngOnInit(): void {
    this.getVendorDetails();
  }

  getVendorDetails() {
    this.apiService.getVendorDetails().subscribe({
      next: (vendorDetails) => {

        const allDays = [0, 1, 2, 3, 4, 5, 6];

        // Deep-copy returned hours
        let workingHours = vendorDetails.working_hours.map(wh => ({ ...wh }));

        // Add missing days
        allDays.forEach(day => {
          if (!workingHours.some(wh => wh.weekday === day)) {
            workingHours.push({
              weekday: day,
              is_open: false,
              open_time: "09:00:00",
              close_time: "22:00:00"
            });
          }
        });

        // Sort by weekday (0–6)
        workingHours = workingHours.sort((a, b) => a.weekday - b.weekday);

        // Set original + modified
        this.originalVendorDetails = {
          ...vendorDetails,
          working_hours: workingHours.map(wh => ({ ...wh }))
        };

        this.modifiedVendorDetails = {
          ...vendorDetails,
          working_hours: workingHours.map(wh => ({ ...wh }))
        };
      },

      error: (err) => {
        this.toastNotificationService.createNotification(this.translate.instant('settings.errors.load_failed_message'), ToastNotificationType.ERROR, this.translate.instant('settings.errors.load_failed_title'), ToastPosition.TOP_RIGHT);
        this.toastNotificationService.createNotification(this.translate.instant('settings.errors.load_failed_message'), ToastNotificationType.ERROR, this.translate.instant('settings.errors.load_failed_title'), ToastPosition.TOP_RIGHT);
      }
    });
  }

  onCityChange(event: any): void {
    this.neighborhoods = [];
    const city = this.cities.find(city => city.id == event.target.value);
    if (city) {
      this.citySelected = true;
      this.neighborhoods = this.cities;
    }
  }

  onCommercialDocumentSelected(event: Event) {
    const fileInput = event.target as HTMLInputElement;
    if (fileInput.files && fileInput.files.length > 0) {
      this.commercialDocument = fileInput.files[0];
    }
  }

  private convertToBase64(file: File): Promise<string> | null {
    if (!this.commercialDocument) return null;
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  }

  // Form Submit
  async submit() {
    if (!this.modifiedVendorDetails) return;
    this.disableButtons = true;
    this.loading = true;

    if (!this.modifiedVendorDetails.logo) {
      this.toastNotificationService.createNotification(this.translate.instant('settings.errors.logo_required_message'), ToastNotificationType.ERROR, this.translate.instant('settings.errors.logo_required_title'), ToastPosition.TOP_RIGHT);
      this.disableButtons = false;
      this.loading = false;
      return;
    }

    if (this.modifiedVendorDetails.logo === this.originalVendorDetails?.logo) {
      this.modifiedVendorDetails.logo = undefined;
    } else if (!this.modifiedVendorDetails.logo && this.originalVendorDetails?.logo) {
      this.modifiedVendorDetails.logo = null;
    }

    if (this.modifiedVendorDetails.banner_image === this.originalVendorDetails?.banner_image) {
      this.modifiedVendorDetails.banner_image = undefined;
    } else if (!this.modifiedVendorDetails.banner_image && this.originalVendorDetails?.banner_image) {
      this.modifiedVendorDetails.banner_image = null;
    }

    if (!this.validateWorkingHours()) {
      this.showWorkingHoursErrors = true;
      this.disableButtons = false;
      this.loading = false;
      return;
    }

    this.showWorkingHoursErrors = false;

    this.apiService.updateVendorDetails(this.modifiedVendorDetails).subscribe({
      next: (vendorDetails) => {
        this.disableButtons = false;
        this.loading = false;
        this.toastNotificationService.createNotification(this.translate.instant('settings.success.updated_message'), ToastNotificationType.SUCCESS, this.translate.instant('settings.success.updated_title'), ToastPosition.TOP_RIGHT);
        this.vendorProfile.set(vendorDetails);
        this.getVendorDetails();
      },
      error: (err) => {
        this.disableButtons = false;
        this.loading = false;
        this.toastNotificationService.createNotification(this.apiErrorService.getDisplayMessage(err, this.translate.instant('settings.errors.update_failed_message')), ToastNotificationType.ERROR, this.translate.instant('settings.errors.update_failed_title'), ToastPosition.TOP_RIGHT);
      }
    });
  }

  areDataModified(): boolean {
    if (!this.originalVendorDetails || !this.modifiedVendorDetails) {
      return false;
    }

    // Compare primitive (non-object) fields
    for (const key in this.originalVendorDetails) {
      if (key === "working_hours") continue;

      const a = this.originalVendorDetails[key as keyof VendorDetails];
      const b = this.modifiedVendorDetails[key as keyof VendorDetails];

      if (JSON.stringify(a) !== JSON.stringify(b)) {
        return true;
      }
    }

    // Compare working hours
    const originalWH = this.originalVendorDetails.working_hours;
    const modifiedWH = this.modifiedVendorDetails.working_hours;

    if (originalWH.length !== modifiedWH.length) return true;

    for (let i = 0; i < originalWH.length; i++) {
      const a = originalWH[i];
      const b = modifiedWH[i];

      if (
        a.is_open !== b.is_open ||
        a.weekday !== b.weekday ||
        a.open_time !== b.open_time ||
        a.close_time !== b.close_time
      ) {
        return true;
      }
    }

    return false;
  }

  validateWorkingHours() {
    if (!this.modifiedVendorDetails)
      return;

    const modifiedWH = this.modifiedVendorDetails.working_hours;

    for (let i = 0; i < modifiedWH.length; i++) {
      const a = modifiedWH[i];

      if (
        a.is_open &&
        (a.open_time.trim() == '' ||
          a.close_time.trim() == '')
      ) {
        return false;
      }
    }

    return true;
  }

  cancel() {
    if (this.originalVendorDetails) {
      this.modifiedVendorDetails = {
        ...this.originalVendorDetails,
        working_hours: this.originalVendorDetails.working_hours.map(wh => ({ ...wh }))
      };
    }
  }

  onPointPicked(event: { lat: number; lng: number }) {
    if (!this.modifiedVendorDetails)
      return;

    this.modifiedVendorDetails.latitude = event.lat;
    this.modifiedVendorDetails.longitude = event.lng;
  }
}
